import { useState } from "react"
import { useTranslation } from "react-i18next"

import { useAgentState } from "@/agent-state"
import { useCleanUp } from "@/components/CallPanel/ACWButtons/hook"
import { SingleClickButton } from "@/components/SingleClickButton"
import { Button } from "@/components/ui/button"
import {
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { useConnectActionsHook } from "@/hooks/connectActions"
import { useContactDataHook } from "@/hooks/contactData"
import { useCallPanelLogger } from "@/hooks/useLogger"
import { useSendCallReport } from "@/hooks/useSendCallReport"

import { CaseWithActivityFeed } from "../student-details-api"

export default function LeavingConfirmation({
  currentCase,
  onClose,
}: {
  currentCase: CaseWithActivityFeed | null
  onClose: (val: boolean) => void
}) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const log = useCallPanelLogger()
  const { send } = useAgentState()
  const { closeContact, hangUpPhoneCall } = useConnectActionsHook()
  const cleanup = useCleanUp()
  const { isVoiceContact, voiceContactId } = useContactDataHook()
  const sendCallReportMutation = useSendCallReport({
    currentCase,
  })

  const sendCallReport = async () => {
    try {
      await sendCallReportMutation.mutateAsync({
        callReportType: "manual-leaving-view",
      })
    } catch {
      // Already logged by mutation onError; avoid double report to Sentry
      return
    }
  }

  const handleNavigate = async () => {
    try {
      setIsLoading(true)
      if (voiceContactId) {
        await hangUpPhoneCall()
      }
      await sendCallReport()
      cleanup()
      if (isVoiceContact && voiceContactId) {
        await closeContact()
      }
      send({ type: "GO_NEXT" })
    } catch (err) {
      log.error(err)
    } finally {
      setIsLoading(false)
      onClose(true)
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>
          {t("global.modals.manualCallLeaveConfirm.title")}
        </DialogTitle>
      </DialogHeader>
      <DialogBody>
        <p>{t("global.modals.manualCallLeaveConfirm.description")}</p>
        <p className="mt-2 font-bold">
          {t("global.modals.manualCallLeaveConfirm.confirmation")}
        </p>
      </DialogBody>
      <DialogFooter>
        <Button variant="outline" onClick={() => onClose(false)}>
          {t("global.button.cancel")}
        </Button>
        <SingleClickButton isLoading={isLoading} onClick={handleNavigate}>
          {t("global.modals.manualCallLeaveConfirm.submit")}
        </SingleClickButton>
      </DialogFooter>
    </DialogContent>
  )
}
